import { Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../../shared/workspace-element-context';
import {
  getAppkitDropdownSelection,
  setDropdownAriaLabel
} from '../../../../shared/utils/appkit-dropdown.util';
import {
  CbcSchdATbl3Service,
  SummaryElement,
  Jurisdiction,
} from './cbc-schd-a-tbl3.service';

@Component({
    selector: 'gtw-cbc-schd-a-tbl3',
    templateUrl: './cbc-schd-a-tbl3.component.html',
    styleUrls: ['./cbc-schd-a-tbl3.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcSchdATbl3Component implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  modalTitle = 'Create Entry';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  saving = false;
  deleting = false;
  isLoading = false;

  mode: 'create' | 'edit' = 'create';

  taxYears: any[] = [];
  selectedTaxYear: any = null;

  summaryElements: SummaryElement[] = [];
  filteredSummaryElements: SummaryElement[] = [];
  summaryDropdownItems: any[] = [];
  selectedElement: SummaryElement | null = null;
  selectedElementItem: any = null;
  elementsLoading = false;
  showCbcSummaryEleRequiredError = false;

  summaryInput = '';
  activate = true;

  jurisdictions: Jurisdiction[] = [];
  selectedJuris: Jurisdiction[] = [];
  jurisLoading = false;
  applyToAllJuris = false;
  hideJurisSelection = false;
  showAppliedJurisRequiredError = false;

  summarySearchText = '';
  jurisDropdownOpen = false;

  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  rowData: any = {};
  private isInitialized = false;

  constructor(
    private tbl3Service: CbcSchdATbl3Service,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = activeScreen?.data?.rowData || this.rowData || {};

    const taxYearValues = this.gtwGlobal?.global?.inputs?.tax_year?.values;
    if (Array.isArray(taxYearValues) && taxYearValues.length > 0) {
      this.taxYears = taxYearValues;
    }
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (
      !this.GENERAL_CONFIG?.base_url ||
      !this.filterParams ||
      Object.keys(this.filterParams).length === 0 ||
      !this.globalService ||
      Object.keys(this.globalService).length === 0
    ) return;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    this.isLoading = true;
    this.cdr.detectChanges();

    this.mode = this.rowData && this.rowData.SNO ? 'edit' : 'create';
    this.modalTitle = this.mode === 'create' ? 'Create Entry' : 'Edit Entry';

    if (!this.taxYears || this.taxYears.length === 0) {
      const taxYearValues = this.gtwGlobal?.global?.inputs?.tax_year?.values;
      this.taxYears = Array.isArray(taxYearValues) ? taxYearValues : [];
    }

    if (this.taxYears.length > 0) {
      const currentTaxYear = this.filterParams.tax_year || this.globalService.tax_year;
      this.selectedTaxYear = this.taxYears.find(
        (ty: any) => ty.value === String(currentTaxYear)
      ) || this.taxYears[0];
    }

    if (this.mode === 'edit') {
      const rd = this.rowData;
      this.selectedTaxYear = this.taxYears.find(
        (ty: any) => ty.value === String(rd.TAX_YEAR)
      ) || this.selectedTaxYear;
      this.summaryInput = rd.CBC_SUMMARY_INPUT || '';
      this.activate = rd.ACTIVATE === 1;
    }

    this.loadSummaryElements();
    this.loadJurisdictions();
  }

  loadSummaryElements(): void {
    if (!this.selectedTaxYear) {
      this.showForm();
      return;
    }
    this.elementsLoading = true;
    this.summaryElements = [];
    this.selectedElement = null;
    this.cdr.detectChanges();

    this.tbl3Service
      .loadSummaryElements(
        this.baseUrl,
        this.ssoId,
        this.selectedTaxYear.value,
        this.selectedTaxYear.scenario,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (elements) => {
          this.summaryElements = elements;
          this.filteredSummaryElements = [...elements];
          this.refreshSummaryDropdownItems();
          if (this.mode === 'edit' && this.rowData.CBC_SUMMARY_VALUE) {
            this.selectedElement = this.getSummaryObj(this.rowData.CBC_SUMMARY_VALUE) || null;
            this.refreshSummaryDropdownItems();
          }
          this.elementsLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while loading summary elements!',
            'gtw-snackbar--red'
          );
          this.elementsLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadJurisdictions(): void {
    if (!this.selectedTaxYear) {
      this.showForm();
      return;
    }
    this.applyToAllJuris = false;
    this.hideJurisSelection = false;
    this.selectedJuris = [];
    this.jurisLoading = true;
    this.cdr.detectChanges();

    this.tbl3Service
      .loadJurisdictions(
        this.baseUrl,
        this.ssoId,
        this.selectedTaxYear.value,
        this.selectedTaxYear.scenario,
        this.globalService,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (jurisdictions) => {
          this.jurisdictions = jurisdictions.map((j) => ({
            ...j,
            id: j.COUNTRY_ID,
          }));
          if (this.mode === 'edit') {
            this.selectedJuris = this.getJurisObjs(this.rowData.APPLIED_JURISDICTIONS);
            this.applyToAllJuris = this.rowData.APPLY_TO_ALL_TAX_JURISDICTIONS === 1;
            this.hideJurisSelection = this.applyToAllJuris;
          }
          this.jurisLoading = false;
          this.showForm();
        },
        error: (err) => {
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while loading jurisdictions!',
            'gtw-snackbar--red'
          );
          this.jurisLoading = false;
          this.showForm();
        },
      });
  }

  private showForm(): void {
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  private getSummaryObj(summaryValue: string): SummaryElement | undefined {
    return this.summaryElements.find((ele) => {
      const cbcSummaryValue = ele.CBC_SUMMARY_ELEMENT
        ? ele.CBC_SUMMARY_VALUE.split(' - ')[1]
        : ele.CBC_SUMMARY_VALUE;
      return cbcSummaryValue === summaryValue;
    });
  }

  private getJurisObjs(appliedJuris: string | null | undefined): Jurisdiction[] {
    if (!appliedJuris || appliedJuris === 'ALL') return [];
    const list = appliedJuris.split(',');
    const result: Jurisdiction[] = [];
    list.forEach((countryId) => {
      const jurisObj = this.jurisdictions.find((j) => j.COUNTRY_ID === countryId);
      if (jurisObj) result.push(jurisObj);
    });
    return result;
  }

  onTaxYearChanged(): void {
    this.loadSummaryElements();
    this.loadJurisdictions();
  }

  onSummarySearch(event: any): void {
    const search = event?.query || event || '';
    this.summarySearchText = search;
    if (!search) {
      this.filteredSummaryElements = [...this.summaryElements];
    } else {
      const lowerSearch = search.toLowerCase();
      const filtered = this.summaryElements.filter((ele) => {
        const display = ele.CBC_SUMMARY_VALUE || '';
        return display.toLowerCase().includes(lowerSearch);
      });
      const userInputItem: SummaryElement = {
        USER_ENTRY: true,
        USER_ENTRY_VALUE: 'Use new: ' + search,
        CBC_SUMMARY_VALUE: search,
        CBC_SUMMARY_ELEMENT: null,
      };
      this.filteredSummaryElements = [userInputItem, ...filtered];
    }
    this.refreshSummaryDropdownItems();
    this.cdr.detectChanges();
  }

  private refreshSummaryDropdownItems(): void {
    this.summaryDropdownItems = (this.filteredSummaryElements || []).map((element, index) => ({
      ...element,
      value: index,
      label: this.getSummaryDisplayValue(element)
    }));
    this.selectedElementItem = this.summaryDropdownItems.find(
      (item) => item === this.selectedElement || item.CBC_SUMMARY_VALUE === this.selectedElement?.CBC_SUMMARY_VALUE
    ) || (this.selectedElement
      ? this.summaryDropdownItems.find((item) => item.CBC_SUMMARY_VALUE === this.selectedElement?.CBC_SUMMARY_VALUE)
      : null);
  }

  onSummaryDropdownSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedElementItem = selected;
    if (!selected) {
      this.onSummaryClear();
      return;
    }
    const element = this.filteredSummaryElements.find(
      (el) => el.CBC_SUMMARY_VALUE === (selected as any).CBC_SUMMARY_VALUE
    );
    if (element) {
      this.onSummarySelect(element);
    }
  }

  onSummarySelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedElementItem = selected;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  onSummarySelect(element: SummaryElement): void {
    this.selectedElement = element;
    this.selectedElementItem = this.summaryDropdownItems.find(
      (item) => item.CBC_SUMMARY_VALUE === element?.CBC_SUMMARY_VALUE
    ) || null;
    this.showCbcSummaryEleRequiredError = false;
    this.cdr.detectChanges();
  }

  onSummaryClear(): void {
    this.selectedElement = null;
    this.selectedElementItem = null;
    this.showCbcSummaryEleRequiredError = true;
    this.cdr.detectChanges();
  }

  getSummaryDisplayValue(element: SummaryElement): string {
    if (!element) return '';
    return element.USER_ENTRY ? (element.USER_ENTRY_VALUE || '') : element.CBC_SUMMARY_VALUE;
  }

  onApplyToAllJurisCheckboxChecked(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.applyToAllJuris = checked;
    this.hideJurisSelection = checked;
    if (checked) {
      this.showAppliedJurisRequiredError = false;
    }
    this.cdr.detectChanges();
  }

  isJurisSelected(juris: Jurisdiction): boolean {
    return this.selectedJuris.some((j) => j.COUNTRY_ID === juris.COUNTRY_ID);
  }

  toggleJuris(juris: Jurisdiction): void {
    const exists = this.selectedJuris.some((j) => j.COUNTRY_ID === juris.COUNTRY_ID);
    if (exists) {
      this.selectedJuris = this.selectedJuris.filter((j) => j.COUNTRY_ID !== juris.COUNTRY_ID);
    } else {
      this.selectedJuris = [...this.selectedJuris, juris];
    }
    this.applyToAllJuris = this.selectedJuris.length === this.jurisdictions.length;
    this.showAppliedJurisRequiredError = this.selectedJuris.length === 0;
    this.cdr.detectChanges();
  }

  selectAllJuris(): void {
    this.selectedJuris = [...this.jurisdictions];
    this.applyToAllJuris = true;
    this.showAppliedJurisRequiredError = false;
    this.cdr.detectChanges();
  }

  unselectAllJuris(): void {
    this.selectedJuris = [];
    this.applyToAllJuris = false;
    this.showAppliedJurisRequiredError = true;
    this.cdr.detectChanges();
  }

  get isFormInvalid(): boolean {
    if (!this.selectedTaxYear) return true;
    if (!this.selectedElement) return true;
    if (!this.applyToAllJuris && this.selectedJuris.length === 0) return true;
    return false;
  }

  private prepareActionCall(actionMode: 'create' | 'edit' | 'delete'): { actionCode: string; clobData: any[]; clobSettings: any[] } {
    const CREATE_ACTION_CODE = 'lj1ccb';
    const UPDATE_ACTION_CODE = 'mo7l6h';
    const DELETE_ACTION_CODE = 'wr3gyp';
    let actionCode: string;
    if (actionMode === 'create') {
      actionCode = CREATE_ACTION_CODE;
    } else if (actionMode === 'edit') {
      actionCode = UPDATE_ACTION_CODE;
    } else {
      actionCode = DELETE_ACTION_CODE;
    }

    const appliedJurisIDs = this.selectedJuris.map((j) => j.COUNTRY_ID);
    const clobSettings = [{
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.filterParams.jcd_key || this.globalService.jcd_key,
    }];
    const clobData: any[] = [{
      TAX_YEAR: this.selectedTaxYear.value,
      CBC_SUMMARY_ELEMENT: this.selectedElement?.CBC_SUMMARY_ELEMENT || null,
      CBC_SUMMARY_VALUE: this.selectedElement?.CBC_SUMMARY_ELEMENT
        ? this.selectedElement.CBC_SUMMARY_VALUE.split(' - ')[1]
        : this.selectedElement?.CBC_SUMMARY_VALUE,
      ACTIVATE: this.activate ? 'Y' : 'N',
      CBC_SUMMARY_INPUT: this.summaryInput || '',
      APPLY_TO_ALL_TAX_JURISDICTIONS: this.applyToAllJuris ? 'Y' : 'N',
      APPLIED_JURISDICTIONS: this.applyToAllJuris ? 'ALL' : appliedJurisIDs.toString(),
    }];

    if (this.mode === 'edit' && this.rowData.SNO) {
      clobData[0].SNO = this.rowData.SNO;
    }

    return { actionCode, clobData, clobSettings };
  }

  save(): void {
    if (this.isSaveClicked || this.crudLoading) return;
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.saving = true;
    this.cdr.detectChanges();

    const { actionCode, clobData, clobSettings } = this.prepareActionCall(this.mode);

    this.tbl3Service
      .saveEntry(this.baseUrl, actionCode, clobData, clobSettings, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.saving = false;
          if (response.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
            this.emitRefreshAndClose();
          } else {
            this.gtwSnackbarService.openSnackBar(
              response.errorMessage || 'Error saving data.',
              'gtw-snackbar--red'
            );
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.saving = false;
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while saving data, please try again!',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }

  delete(): void {
    if (this.crudLoading) return;
    this.crudLoading = true;
    this.deleting = true;
    this.cdr.detectChanges();

    const { actionCode, clobData, clobSettings } = this.prepareActionCall('delete');

    this.tbl3Service
      .saveEntry(this.baseUrl, actionCode, clobData, clobSettings, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.crudLoading = false;
          this.deleting = false;
          if (response.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Data deleted successfully.', 'gtw-snackbar--green');
            this.emitRefreshAndClose();
          } else {
            this.gtwSnackbarService.openSnackBar(
              response.errorMessage || 'Error deleting data.',
              'gtw-snackbar--red'
            );
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.crudLoading = false;
          this.deleting = false;
          this.gtwSnackbarService.openSnackBar(
            err?.message || 'Something went wrong while deleting the data, please try again!',
            'gtw-snackbar--red'
          );
          this.cdr.detectChanges();
        },
      });
  }

  private emitRefreshAndClose(): void {
    this.cancel();
    setTimeout(() => {
      const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
      if (activeScreen?.screen_key) {
        this.gtwWorkspace.refreshGridData.next(activeScreen);
      }
    }, 500);
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-schd-a-tbl3-ce')
      ?.dispatchEvent(updateEvent);
  }
}
